import { API_BASE_URL } from '@synergy/config';
import { Box, Stack } from '@mui/material';
import { ConfirmationDialog, PoolButton, PoolFormattedMessage, PoolModal, PoolSelect } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { PoolSelectOption, StyledMobileStepper } from '@synergy/components-next';
import { ReactElement, ReactNode, useEffect, useState } from 'react';

import { Employee } from './models/employee';
import { UserManagementApi } from '../../common/api/user-management-api';
import { useSelector } from '../../store/use-appstate-selector';

export interface UserManagementDeleteModalProps {
    isOpen: boolean;
    user?: Employee;
    onClose: () => void;
    onConfirm: (
        user: Employee,
        reassignMainContactId?: number,
        reassignBonusContactId?: number,
        reassignImportContactId?: number
    ) => void;
}

const api = new UserManagementApi(API_BASE_URL);

enum StepType {
    MainContact,
    BonusContact,
    ImportContact,
}

interface Step {
    type: StepType;
    content: ReactNode;
}

export const UserManagementDeleteModal = (props: UserManagementDeleteModalProps): ReactElement | null => {
    const { isOpen, user, onClose, onConfirm } = props;
    const accountId = useSelector(state => state.currentAccount?.id);
    const [options, setOptions] = useState<PoolSelectOption<number>[]>([]);
    const [selectedBonusEmployeeId, setSelectedBonusEmployeeId] = useState<number>();
    const [selectedMainEmployeeId, setSelectedMainEmployeeId] = useState<number>();
    const [selectedImportContactEmployeeId, setSelectedImportContactEmployeeId] = useState<number>();
    const [steps, setSteps] = useState<Step[]>([]);
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        if (!isOpen || !user || !accountId) return;

        const fetchContacts = async () => {
            const params = new URLSearchParams({ limit: '1000' });
            const response = await api.cget(accountId, params);
            if (response.success) {
                const options = response.result.items
                    .filter(contact => contact.id !== user.id)
                    .map<PoolSelectOption<number>>(contact => ({
                        value: contact.id,
                        displayValue: contact.fullName,
                    }));
                setOptions(options);
            } else {
                setOptions([]);
            }
        };
        fetchContacts();
    }, [accountId, isOpen, user]);

    useEffect(() => {
        if (!isOpen || !user) return;

        const newSteps = [];
        if (user.isMainContact) {
            newSteps.push({
                type: StepType.MainContact,
                content: (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <PoolFormattedMessage
                                id="app.user.management.delete.employee.mainContactText"
                                values={{ value: user.fullName }}
                            />
                        </Box>
                        <PoolSelect
                            options={options}
                            fullWidth={true}
                            value={selectedMainEmployeeId ?? ''}
                            onValueChange={setSelectedMainEmployeeId}
                            label={<FormattedMessage id="app.user.management.delete.employee.mainContactSelect" />}
                            disallowDefaultValue={true}
                        />
                    </>
                ),
            });
        }
        if (user.hasBonusConditions) {
            newSteps.push({
                type: StepType.BonusContact,
                content: (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <PoolFormattedMessage
                                id="app.user.management.delete.employee.bonusText"
                                values={{ value: user.fullName }}
                            />
                        </Box>
                        <PoolSelect
                            options={options}
                            fullWidth={true}
                            value={selectedBonusEmployeeId ?? ''}
                            onValueChange={setSelectedBonusEmployeeId}
                            label={<FormattedMessage id="app.user.management.delete.employee.bonusSelect" />}
                            disallowDefaultValue={true}
                        />
                    </>
                ),
            });
        }
        if (user.isImportContact) {
            newSteps.push({
                type: StepType.ImportContact,
                content: (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <PoolFormattedMessage
                                id="app.user.management.delete.employee.importText"
                                values={{ value: user.fullName }}
                            />
                        </Box>
                        <PoolSelect
                            options={options}
                            fullWidth={true}
                            value={selectedImportContactEmployeeId ?? ''}
                            onValueChange={setSelectedImportContactEmployeeId}
                            label={<FormattedMessage id="app.user.management.delete.employee.importContactSelect" />}
                            disallowDefaultValue={true}
                        />
                    </>
                ),
            });
        }

        setSteps(newSteps);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, options, selectedMainEmployeeId, selectedBonusEmployeeId, selectedImportContactEmployeeId]);

    if (!user) {
        return null;
    }

    const disabledStep =
        (steps[activeStep]?.type === StepType.BonusContact && !selectedBonusEmployeeId) ||
        (steps[activeStep]?.type === StepType.MainContact && !selectedMainEmployeeId) ||
        (steps[activeStep]?.type === StepType.ImportContact && !selectedImportContactEmployeeId);

    if (steps.length === 0) {
        return (
            <ConfirmationDialog
                title={<FormattedMessage id="app.user.management.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                isVisible={isOpen}
                onCancel={onClose}
                onConfirm={() => onConfirm(user)}
            >
                <PoolFormattedMessage
                    id="app.user.management.delete.employee.message"
                    values={{ value: user?.fullName }}
                />
            </ConfirmationDialog>
        );
    }

    if (steps.length === 1) {
        return (
            <PoolModal
                title={<FormattedMessage id="app.user.management.delete" />}
                isVisible={isOpen}
                handleClose={onClose}
                actionChildren={[
                    <span key="1" />,
                    <PoolButton
                        key="2"
                        color="secondary"
                        styleType="firstLevel"
                        disabled={disabledStep}
                        onClick={() =>
                            onConfirm(
                                user,
                                selectedMainEmployeeId,
                                selectedBonusEmployeeId,
                                selectedImportContactEmployeeId
                            )
                        }
                    >
                        <FormattedMessage id="app.common.delete" />
                    </PoolButton>,
                ]}
            >
                <Stack sx={{ textAlign: 'center' }}>{steps[activeStep]?.content}</Stack>
            </PoolModal>
        );
    }

    return (
        <PoolModal
            title={<FormattedMessage id="app.user.management.delete" />}
            isVisible={isOpen}
            handleClose={onClose}
            actionChildren={
                <StyledMobileStepper
                    activeStep={activeStep}
                    steps={steps.length}
                    variant="dots"
                    backButton={
                        <PoolButton
                            variant="text"
                            color="primary"
                            styleType="firstLevel"
                            disabled={activeStep === 0}
                            onClick={() => setActiveStep(prevActiveStep => prevActiveStep - 1)}
                        >
                            <FormattedMessage id="app.common.back" />
                        </PoolButton>
                    }
                    nextButton={
                        activeStep === steps.length - 1 ? (
                            <PoolButton
                                color="secondary"
                                styleType="firstLevel"
                                disabled={disabledStep}
                                onClick={() =>
                                    onConfirm(
                                        user,
                                        selectedBonusEmployeeId,
                                        selectedMainEmployeeId,
                                        selectedImportContactEmployeeId
                                    )
                                }
                            >
                                <FormattedMessage id="app.common.delete" />
                            </PoolButton>
                        ) : (
                            <PoolButton
                                color="primary"
                                styleType="firstLevel"
                                disabled={disabledStep}
                                onClick={() => setActiveStep(prevActiveStep => prevActiveStep + 1)}
                            >
                                <FormattedMessage id="app.common.next" />
                            </PoolButton>
                        )
                    }
                />
            }
        >
            <Stack sx={{ textAlign: 'center' }}>{steps[activeStep]?.content}</Stack>
        </PoolModal>
    );
};
